/**
 * Approximate k-means segment assignment for individual respondents.
 *
 * The conjoint study identified 3 segments via k-means clustering on part-worth
 * utilities, but the raw data doesn't include per-respondent cluster labels.
 * We approximate membership using observable behavioral patterns that align
 * with each segment's known profile:
 *
 * - Low-Interest Prospects (28.4%): near-zero demand at any price → high none count
 * - Budget Seekers (19.0%): high demand at $499, cliff above → price distribution skewed to $499
 * - Price-Sensitive Buyers (52.6%): moderate demand across prices → everyone else
 */

import type { VerbatimRespondent } from '../data/verbatimData';
import { VERBATIM_DATA } from '../data/verbatimData';

export type KMeansSegment = 'Price-Sensitive Buyers' | 'Low-Interest Prospects' | 'Budget Seekers';

export interface AssignedRespondent extends VerbatimRespondent {
  kmeansSegment: KMeansSegment;
}

export function assignSegment(r: VerbatimRespondent): KMeansSegment {
  const totalChoices = 15 - r.noneCount;

  if (r.noneCount >= 12) {
    return 'Low-Interest Prospects';
  }

  if (totalChoices > 0) {
    const lowPriceChoices = (r.priceDistribution['499'] || 0);
    const lowPriceShare = lowPriceChoices / totalChoices;
    if (lowPriceShare > 0.5 && totalChoices >= 3) {
      return 'Budget Seekers';
    }
  }

  return 'Price-Sensitive Buyers';
}

let _cache: AssignedRespondent[] | null = null;

export function getAssignedRespondents(): AssignedRespondent[] {
  if (_cache) return _cache;
  _cache = VERBATIM_DATA.map(r => ({ ...r, kmeansSegment: assignSegment(r) }));
  return _cache;
}

export function getRespondentsBySegment(): Record<KMeansSegment, AssignedRespondent[]> {
  const all = getAssignedRespondents();
  const result: Record<KMeansSegment, AssignedRespondent[]> = {
    'Price-Sensitive Buyers': [],
    'Low-Interest Prospects': [],
    'Budget Seekers': [],
  };
  for (const r of all) {
    result[r.kmeansSegment].push(r);
  }
  return result;
}

export function computeDemographicProfile(respondents: VerbatimRespondent[]) {
  const n = respondents.length;
  if (n === 0) return null;

  const genderCount: Record<string, number> = {};
  const incomeCount: Record<string, number> = {};
  const regionCount: Record<string, number> = {};
  const goalCount: Record<string, number> = {};
  const spendCount: Record<string, number> = {};
  const raceCount: Record<string, number> = {};
  const employmentCount: Record<string, number> = {};
  const relationshipCount: Record<string, number> = {};
  const educationCount: Record<string, number> = {};
  let totalAge = 0;
  let age18_34 = 0, age35_49 = 0, age50plus = 0;
  let totalNone = 0;
  let totalChosenPrice = 0;
  let chosenPriceCount = 0;

  const attitudeAgg: Record<string, { agree: number; total: number }> = {};
  const factorRankAgg: Record<string, { sum: number; count: number }> = {};
  const featureRankAgg: Record<string, { sum: number; count: number }> = {};
  const brandCount: Record<string, number> = {};
  const deviceCount: Record<string, number> = {};

  for (const r of respondents) {
    inc(genderCount, r.gender);
    inc(incomeCount, r.income);
    inc(regionCount, r.region);
    inc(goalCount, r.primaryGoal);
    inc(spendCount, r.monthlySpend);
    inc(raceCount, r.race);
    inc(employmentCount, r.employmentStatus);
    inc(relationshipCount, r.relationshipStatus);
    inc(educationCount, r.education);

    totalAge += r.age;
    if (r.age < 35) age18_34++;
    else if (r.age < 50) age35_49++;
    else age50plus++;

    totalNone += r.noneCount;
    if (r.avgChosenPrice !== null) {
      totalChosenPrice += r.avgChosenPrice;
      chosenPriceCount++;
    }

    if (r.attitudes) {
      for (const [stmt, val] of Object.entries(r.attitudes)) {
        if (!attitudeAgg[stmt]) attitudeAgg[stmt] = { agree: 0, total: 0 };
        attitudeAgg[stmt].total++;
        if (val === 'Agree' || val === 'Strongly agree') attitudeAgg[stmt].agree++;
      }
    }

    if (r.purchaseFactorRanking) {
      for (const [factor, rank] of Object.entries(r.purchaseFactorRanking)) {
        if (!factorRankAgg[factor]) factorRankAgg[factor] = { sum: 0, count: 0 };
        if (rank > 0) { factorRankAgg[factor].sum += rank; factorRankAgg[factor].count++; }
      }
    }

    if (r.featureRanking) {
      for (const [feat, rank] of Object.entries(r.featureRanking)) {
        if (!featureRankAgg[feat]) featureRankAgg[feat] = { sum: 0, count: 0 };
        if (rank > 0) { featureRankAgg[feat].sum += rank; featureRankAgg[feat].count++; }
      }
    }

    if (r.brandConsideration) {
      for (const b of r.brandConsideration) inc(brandCount, b);
    }
    if (r.wearableDevices) {
      for (const d of r.wearableDevices) inc(deviceCount, d);
    }
  }

  return {
    count: n,
    gender: topN(genderCount, 3),
    avgAge: Math.round(totalAge / n),
    ageBands: { '18-34': pct(age18_34, n), '35-49': pct(age35_49, n), '50+': pct(age50plus, n) },
    income: topN(incomeCount, 3),
    race: topN(raceCount, 3),
    education: topN(educationCount, 3),
    employment: topN(employmentCount, 3),
    relationship: topN(relationshipCount, 3),
    region: topN(regionCount, 5),
    primaryGoal: topN(goalCount, 3),
    monthlySpend: topN(spendCount, 4),
    avgNoneRate: +(totalNone / n / 15).toFixed(3),
    avgChosenPrice: chosenPriceCount > 0 ? Math.round(totalChosenPrice / chosenPriceCount) : null,
    attitudes: Object.entries(attitudeAgg)
      .map(([stmt, { agree, total }]) => ({ statement: stmt, agreeRate: pct(agree, total) }))
      .sort((a, b) => b.agreeRate - a.agreeRate),
    purchaseFactors: Object.entries(factorRankAgg)
      .map(([factor, { sum, count }]) => ({ factor, avgRank: count > 0 ? +(sum / count).toFixed(1) : 0 }))
      .sort((a, b) => a.avgRank - b.avgRank),
    topFeatures: Object.entries(featureRankAgg)
      .map(([feat, { sum, count }]) => ({ feature: feat, avgRank: count > 0 ? +(sum / count).toFixed(1) : 0 }))
      .sort((a, b) => a.avgRank - b.avgRank),
    brands: topN(brandCount, 6),
    devices: topN(deviceCount, 6),
  };
}

function inc(map: Record<string, number>, key: string) {
  if (!key) return;
  map[key] = (map[key] || 0) + 1;
}

function pct(count: number, total: number): number {
  return total > 0 ? +((count / total) * 100).toFixed(1) : 0;
}

function topN(map: Record<string, number>, n: number): { label: string; count: number; pct: number }[] {
  const total = Object.values(map).reduce((s, v) => s + v, 0);
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([label, count]) => ({ label, count, pct: pct(count, total) }));
}
